import {
  ACESFilmicToneMapping,
  AmbientLight,
  DirectionalLight,
  HemisphereLight,
  PerspectiveCamera,
  Scene,
  WebGLRenderer,
} from 'three'
import type { CharacterKind } from '@/components'
import type { AssetLibrary } from '@/gfx/assets/AssetLoader'
import { CharacterRig } from './character'
import { CHARACTER_KINDS } from './characterKinds'
import { makeRadialTexture } from './textures'

/**
 * Renders each body plan to a still image for the menu.
 *
 * A fighter is a silhouette seen from a fixed overhead camera at arena scale;
 * describing one in words ("бочка на ножках") is not the same as showing it, and
 * picking a character you have never seen is not a choice. These are rendered
 * once at startup with a throwaway renderer and handed to the menu as data URLs
 * — no second live canvas, no per-frame cost.
 *
 * They are rendered in the asking player's colour, because the colour is half
 * of what you are choosing.
 */
export function renderModelPreviews(
  color: number,
  width = 150,
  height = 176,
  assets?: AssetLibrary,
): Record<CharacterKind, string> {
  const renderer = new WebGLRenderer({ antialias: true, alpha: true })
  renderer.setSize(width, height, false)
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
  renderer.toneMapping = ACESFilmicToneMapping
  renderer.toneMappingExposure = 1.25

  const scene = new Scene()

  // Lit from the front-left so the near side reads and the far side falls into
  // shadow — the same modelling the arena uses, at portrait distance.
  const key = new DirectionalLight(0xffffff, 3.4)
  key.position.set(-2.4, 3.2, 3)
  const fill = new HemisphereLight(0x8899aa, 0x201f1a, 1.4)
  scene.add(key, fill, new AmbientLight(0x445055, 1.1))

  // Raised to look down at about 35°. A level portrait camera showed the
  // fighter's chin and none of the face: the face is deliberately rolled back
  // toward the crown so it is visible from the arena's overhead view, and a
  // preview that does not share that angle is a preview of a different model.
  const camera = new PerspectiveCamera(34, width / height, 0.1, 40)
  camera.position.set(1.5, 2.9, 4.4)
  camera.lookAt(0, 0.95, 0)

  const glow = makeRadialTexture(1, 0, 2.4)
  const previews = {} as Record<CharacterKind, string>

  for (const kind of CHARACTER_KINDS) {
    // The picker must show the fighter the player will actually get. When a
    // model is registered for this kind, the portrait comes from the model;
    // otherwise the procedural rig stands in.
    const imported = assets?.instantiateFighter(kind, color) ?? null
    if (imported) {
      imported.rotation.y = 0.42
      scene.add(imported)
      renderer.render(scene, camera)
      previews[kind] = renderer.domElement.toDataURL('image/png')
      scene.remove(imported)
      continue
    }

    const rig = new CharacterRig(color, glow, kind)
    // The ground glow and instability halo belong to the arena, not a portrait.
    rig.setPortraitMode()
    rig.group.rotation.y = 0.42
    scene.add(rig.group)

    renderer.render(scene, camera)
    previews[kind] = renderer.domElement.toDataURL('image/png')

    scene.remove(rig.group)
    rig.dispose()
  }

  glow.dispose()
  renderer.dispose()
  return previews
}
